import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createSku,
  setSkuModule,
  skuModulesQuery,
  skuQuery,
  skusShellQuery,
  skusTableQuery,
  updateSku,
  type AlarmGroup,
  type SkuForm,
  type SkuRow,
} from '../../api/settingsEaseedeskSkus'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { SkuCommandsTab, SkuTicketSettingsTab, SkuWorkflowsTab } from './SkuTabs'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → SKUs.
 *
 * Element cu element fata de `version-3/easeedesk_settings/skus/list.php` +
 * `edit.php` + `modules.php`, si gest cu gest fata de fisierele `*-1.js` de
 * langa ele.
 *
 * `support_license` are TREI intelesuri intr-o singura coloana, ca in portal:
 * `-1` nelimitat, `0` nelicentiat, iar orice numar pozitiv e numarul de zile,
 * scris in casuta de alaturi. De-aia sunt doua campuri in formular si o
 * singura coloana in baza.
 *
 * FILELE unui SKU - Modules, Commands, Ticket settings, Workflows - se deschid
 * sub randul lui, ca bara de file din `skus/tabs.php`. In portal erau o fisa
 * separata adusa prin ajax; aici randul se desface pe loc, ca la Modules, si
 * lista ramane sub ochi.
 */

const PAGE = 25

const DEVICE_TYPES = [
  { value: 1, label: 'Laptop/PC' },
  { value: 2, label: 'Chromebook' },
  { value: 3, label: 'MAC' },
]

const EMPTY: SkuForm = {
  sku: '',
  sku_name: '',
  sku_type: 1,
  device_type: 0,
  support_license: 0,
  support_license_days: 0,
  proactive_support: 0,
  proactive_alarms_group: 0,
  devices: 0,
  auto_activation: 0,
  auto_assign_to_customer: 0,
  visible: 0,
  run_with_adam: 0,
}

function formOf(sku: SkuRow): SkuForm {
  const license = Number(sku.support_license)
  const banded = license === -1 || license === 0
  return {
    sku: String(sku.sku ?? ''),
    sku_name: sku.sku_name ?? '',
    sku_type: Number(sku.sku_type) === 2 ? 2 : 1,
    device_type: Number(sku.device_type ?? 0),
    support_license: banded ? license : 1,
    support_license_days: banded ? 0 : license,
    proactive_support: Number(sku.proactive_support ?? 0),
    proactive_alarms_group: Number(sku.proactive_alarms_group ?? 0),
    devices: Number(sku.devices ?? 0),
    auto_activation: Number(sku.auto_activation ?? 0),
    auto_assign_to_customer: Number(sku.auto_assign_to_customer ?? 0),
    visible: Number(sku.visible ?? 0),
    run_with_adam: Number(sku.run_with_adam ?? 0),
  }
}

function Switch({
  label,
  name,
  on,
  onChange,
}: {
  label: string
  name: string
  on: boolean
  onChange(next: boolean): void
}) {
  return (
    <div className="form-group">
      <input
        type="checkbox"
        className="switchery_edit"
        name={name}
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
      />{' '}
      <label className="control-label">{label}</label>
    </div>
  )
}

function SkuModal({
  sku,
  alarmGroups,
  open,
  onClose,
  onSaved,
}: {
  sku?: SkuRow
  alarmGroups: AlarmGroup[]
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = sku !== undefined
  const existing = useQuery(skuQuery(open && editing ? sku.ID : 0))
  const [form, setForm] = useState<SkuForm>(EMPTY)

  useEffect(() => {
    if (!open) return
    if (!editing) {
      setForm(EMPTY)
      return
    }
    if (existing.data) setForm(formOf(existing.data.sku))
  }, [open, editing, existing.data])

  const save = useMutation({
    mutationFn: () => (editing ? updateSku(sku.ID, form) : createSku(form)),
    onSuccess: () => {
      toast(editing ? 'SKU updated.' : 'SKU created.', 'SKUs')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const set = (patch: Partial<SkuForm>) => setForm({ ...form, ...patch })
  const daysDisabled = form.support_license === -1 || form.support_license === 0

  return (
    <Modal
      id={editing ? 'editSkuModal' : 'newSkuModal'}
      labelledBy={editing ? 'editSkuModalLabel' : 'newSkuModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit SKU' : 'New SKU'}
    >
      <form
        id={editing ? 'edit-sku-form' : 'new-sku-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (form.sku.trim() === '' || form.sku_name.trim() === '' || form.device_type === 0) return
          save.mutate()
        }}
      >
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label className="control-label">
                SKU <span className="required">*</span>
              </label>
              <input
                type="number"
                className="form-control input-sm"
                name="sku"
                value={form.sku}
                onChange={(e) => set({ sku: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="control-label">
                SKU Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control input-sm"
                name="sku_name"
                maxLength={200}
                value={form.sku_name}
                onChange={(e) => set({ sku_name: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="control-label">
                SKU Type <span className="required">*</span>
              </label>
              <select
                className="form-control input-sm"
                name="sku_type"
                value={form.sku_type}
                onChange={(e) => set({ sku_type: Number(e.target.value) })}
              >
                <option value={1}>Standard</option>
                <option value={2}>Subscription</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <Switch
              label="Proactive Support"
              name="proactive_support"
              on={form.proactive_support === 1}
              onChange={(v) => set({ proactive_support: v ? 1 : 0 })}
            />
          </div>
          <div className="col-md-8">
            <div className="form-group">
              <select
                className="form-control input-sm"
                name="proactive_alarms_group"
                value={form.proactive_alarms_group}
                onChange={(e) => set({ proactive_alarms_group: Number(e.target.value) })}
              >
                <option value={0}> - choose proactive alarm group - </option>
                {alarmGroups.map((group) => (
                  <option key={group.ID} value={group.ID}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-3">
            <div className="form-group">
              <label className="control-label">Support License</label>
              <select
                className="form-control input-sm"
                name="support_license"
                value={form.support_license}
                onChange={(e) => set({ support_license: Number(e.target.value) })}
              >
                <option value={-1}>Unlimited</option>
                <option value={0}>Unlicesed</option>
                <option value={1}>Number Of Days</option>
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="control-label">Number Of Days (Support License)</label>
              <input
                type="number"
                className="form-control input-sm"
                name="support_license_days"
                disabled={daysDisabled}
                value={daysDisabled ? '' : form.support_license_days}
                onChange={(e) => set({ support_license_days: Number(e.target.value) })}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="control-label">Number Of Devices</label>
              <input
                type="number"
                className="form-control input-sm"
                name="devices"
                value={form.devices}
                onChange={(e) => set({ devices: Number(e.target.value) })}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label className="control-label">
                Device Type <span className="required">*</span>
              </label>
              <select
                className="form-control input-sm"
                name="device_type"
                value={form.device_type}
                onChange={(e) => set({ device_type: Number(e.target.value) })}
              >
                <option value={0}> - choose - </option>
                {DEVICE_TYPES.map((row) => (
                  <option key={row.value} value={row.value}>
                    {row.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <Switch
              label="Auto Assign To Customer"
              name="auto_assign_to_customer"
              on={form.auto_assign_to_customer === 1}
              onChange={(v) => set({ auto_assign_to_customer: v ? 1 : 0 })}
            />
          </div>
          <div className="col-md-4">
            <Switch
              label="Auto Activation"
              name="auto_activation"
              on={form.auto_activation === 1}
              onChange={(v) => set({ auto_activation: v ? 1 : 0 })}
            />
          </div>
          <div className="col-md-4">
            <Switch
              label="Visible"
              name="visible"
              on={form.visible === 1}
              onChange={(v) => set({ visible: v ? 1 : 0 })}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <Switch
              label="Run with Adam"
              name="run_with_adam"
              on={form.run_with_adam === 1}
              onChange={(v) => set({ run_with_adam: v ? 1 : 0 })}
            />
          </div>
        </div>

        <hr className="dotted" />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-success"
            disabled={
              form.sku.trim() === '' || form.sku_name.trim() === '' || form.device_type === 0 || save.isPending
            }
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** Fila Modules a unui SKU: `sku_modules` + `save_sku_module_status`. */
function SkuModules({ sku }: { sku: number }) {
  const queryClient = useQueryClient()
  const data = useQuery(skuModulesQuery(sku))

  const flip = useMutation({
    mutationFn: (v: { module: number; on: boolean }) => setSkuModule(sku, v.module, v.on),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'skus', 'modules', sku] })
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (!data.data) return <Spinner />

  return (
    <table className="table table-hover">
      <thead>
        <tr>
          <th className="a-left">Module</th>
          <th className="a-left" style={{ width: 250, textAlign: 'center' }}>
            Status
          </th>
        </tr>
      </thead>
      <tbody>
        {data.data.modules.map((module) => (
          <tr key={module.id}>
            <td className="a-left">
              <strong>{module.name}</strong>
            </td>
            <td className="a-left" style={{ textAlign: 'center' }}>
              <input
                type="checkbox"
                name="module"
                value={module.id}
                data-sku={sku}
                checked={data.data.enabled.includes(module.id)}
                disabled={flip.isPending}
                onChange={(e) => flip.mutate({ module: module.id, on: e.target.checked })}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Filele fisei unui SKU, in ordinea din `skus/tabs.php`. */
type SkuTab = 'modules' | 'commands' | 'ticket_settings' | 'workflows'

const SKU_TABS: { key: SkuTab; label: string }[] = [
  { key: 'modules', label: 'Modules' },
  { key: 'commands', label: 'Commands' },
  { key: 'ticket_settings', label: 'Ticket Settings' },
  { key: 'workflows', label: 'Workflows' },
]

function SkuTabStrip({
  sku,
  tab,
  onTab,
  onSaved,
}: {
  sku: number
  tab: SkuTab
  onTab(next: SkuTab): void
  onSaved(): void
}) {
  return (
    <div>
      <ul className="nav nav-tabs">
        {SKU_TABS.map((one) => (
          <li key={one.key} className={tab === one.key ? 'active' : undefined}>
            <a style={{ cursor: 'pointer' }} onClick={() => onTab(one.key)}>
              {one.label}
            </a>
          </li>
        ))}
      </ul>
      <div className="tab-content" style={{ paddingTop: 15 }}>
        {tab === 'modules' && <SkuModules sku={sku} />}
        {tab === 'commands' && <SkuCommandsTab sku={sku} onSaved={onSaved} />}
        {tab === 'ticket_settings' && <SkuTicketSettingsTab sku={sku} onSaved={onSaved} />}
        {tab === 'workflows' && <SkuWorkflowsTab sku={sku} onSaved={onSaved} />}
      </div>
    </div>
  )
}

export function SkusPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(skusShellQuery())
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<SkuRow | null>(null)
  const [open, setOpen] = useState<{ sku: number; tab: SkuTab } | null>(null)

  const table = useQuery(skusTableQuery(search, start, PAGE))

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'skus'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The SKUs screen" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const rows = table.data?.data ?? []
  const total = table.data?.recordsTotal ?? 0

  return (
    <div>
      <h4>
        <strong>SKUs</strong> listing
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>SKUs</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create SKU
      </button>
      <hr />

      <div className="form-group" style={{ maxWidth: 320 }}>
        <input
          type="search"
          className="form-control input-sm"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setStart(0)
            setSearch(e.target.value)
          }}
        />
      </div>

      {table.error ? (
        <LoadFailed what="The SKUs" error={table.error} onRetry={() => void table.refetch()} />
      ) : !table.data ? (
        <Spinner />
      ) : (
        <table className="table table-bordered dataTable" width="100%" id="skus-list">
          <thead>
            <tr>
              <th className="a-left">SKU</th>
              <th className="a-left">Name</th>
              <th className="a-left">Type</th>
              <th className="a-left">Adam</th>
              <th className="a-left">Support license</th>
              <th className="a-left">Proactive</th>
              <th className="a-left">Alarm group</th>
              <th className="a-left">Devices</th>
              <th className="a-left">Auto Activation</th>
              <th className="a-left">Commands</th>
              <th className="a-left">Device Type</th>
              <th width={200}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12} style={{ textAlign: 'center' }}>
                  Nothing to see yet.
                </td>
              </tr>
            ) : (
              rows.map((sku) => (
                <Fragment key={sku.ID}>
                  <tr id={`row_${sku.ID}`}>
                    <td>{sku.sku}</td>
                    <td>{sku.sku_name}</td>
                    <td>{Number(sku.sku_type) === 2 ? 'Subscription' : 'Standard'}</td>
                    <td>{Number(sku.run_with_adam) === 1 ? 'Yes' : 'No'}</td>
                    <td>
                      {Number(sku.support_license) === -1
                        ? 'Unlimited'
                        : Number(sku.support_license) === 0
                          ? 'Unlicesed'
                          : `${sku.support_license} days`}
                    </td>
                    <td>{Number(sku.proactive_support) === 1 ? 'Yes' : 'No'}</td>
                    <td>{sku.group_name ?? ''}</td>
                    <td>{sku.devices}</td>
                    <td>{Number(sku.auto_activation) === 1 ? 'Yes' : 'No'}</td>
                    <td>
                      <span className="label label-info">{sku.commands_count ?? 0}</span>
                    </td>
                    <td>{DEVICE_TYPES.find((one) => one.value === Number(sku.device_type))?.label ?? ''}</td>
                    <td className="center">
                      <button type="button" className="btn btn-xs btn-default" onClick={() => setEdited(sku)}>
                        <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn-xs btn-default"
                        onClick={() =>
                          setOpen(open?.sku === sku.ID ? null : { sku: sku.ID, tab: 'modules' })
                        }
                      >
                        <i className="fas fa-th-large" aria-hidden="true"></i> Tabs
                      </button>
                    </td>
                  </tr>
                  {open?.sku === sku.ID && (
                    <tr>
                      <td colSpan={12}>
                        <SkuTabStrip
                          sku={sku.ID}
                          tab={open.tab}
                          onTab={(tab) => setOpen({ sku: sku.ID, tab })}
                          onSaved={reload}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      )}

      {total > PAGE && (
        <div className="aright">
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start === 0}
            onClick={() => setStart(Math.max(0, start - PAGE))}
          >
            Previous
          </button>{' '}
          <span>
            {start + 1}-{Math.min(start + PAGE, total)} of {total}
          </span>{' '}
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start + PAGE >= total}
            onClick={() => setStart(start + PAGE)}
          >
            Next
          </button>
        </div>
      )}

      <SkuModal
        alarmGroups={shell.data.alarm_groups}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
      <SkuModal
        sku={edited ?? undefined}
        alarmGroups={shell.data.alarm_groups}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}
